import { Check, ArrowUpRight, Calendar, Headphones } from "lucide-react";
import { BOOKING, buildPaypalUrl } from "../i18n";
import { openCalendly } from "../utils/calendly";
import Reveal from "./Reveal";

function Cta({ item, testId, variant = "primary" }) {
  const baseClass =
    "inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium border-b pb-1 transition-colors";
  const colorClass =
    variant === "secondary"
      ? "text-[#8D7B68] border-transparent hover:text-[#3E362E] hover:border-[#8D7B68]/50"
      : "text-[#1A9BC1] border-[#1A9BC1]/40 hover:border-[#1A9BC1]";

  if (!item) return null;

  if (item.type === "paypal") {
    const returnUrl =
      typeof window !== "undefined"
        ? `${window.location.origin}${window.location.pathname}?paid=true#therapies`
        : "";
    const href = buildPaypalUrl(returnUrl, {
      amount: item.amount,
      itemName: item.item,
    });
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={`${baseClass} ${colorClass}`}
        data-testid={testId}
      >
        {item.label}
        <ArrowUpRight size={14} />
      </a>
    );
  }

  return (
    <a
      href={item.href}
      className={`${baseClass} ${colorClass}`}
      data-testid={testId}
    >
      {item.label}
      <ArrowUpRight size={14} />
    </a>
  );
}

export default function Therapies({ t }) {
  const handleDiscovery = () => openCalendly(BOOKING.calendlyDiscovery);

  return (
    <section
      id="therapies"
      data-testid="therapies-section"
      className="py-28 md:py-40 bg-[#FDFBF7]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="overline">{t.therapies.overline}</span>
            </Reveal>
            <Reveal delay={120}>
              <h2
                className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.05] text-[#3E362E] whitespace-pre-line"
                data-testid="therapies-title"
              >
                {t.therapies.title}
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <button
                type="button"
                onClick={handleDiscovery}
                className="btn-accent mt-8"
                data-testid="therapies-cta-discovery"
              >
                <Calendar size={16} />
                {t.therapies.headerCta}
              </button>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pt-8">
            <Reveal delay={300}>
              <p className="text-[#5E554C] text-lg font-light leading-relaxed">
                {t.therapies.intro}
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-[#E8E1D9]">
          {t.therapies.items.map((s, i) => (
            <Reveal key={s.tag} delay={i * 120}>
              <article
                className="service-card !border-t-0 !border-l-0 h-full min-h-[420px] flex flex-col justify-between"
                data-testid={`therapy-card-${i}`}
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="section-number">{s.tag}</span>
                    <span
                      className={`h-px w-16 ${i % 2 === 0 ? "bg-[#1A9BC1]" : "bg-[#C99A4E]"}`}
                    />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#3E362E] mb-3 leading-tight">
                    {s.title}
                  </h3>
                  <p className="overline !tracking-[0.18em] mb-5">{s.meta}</p>
                  <p className="text-[#5E554C] text-base font-light leading-relaxed">
                    {s.desc}
                  </p>
                </div>
                <div className="mt-8">
                  <ul className="flex flex-wrap gap-x-6 gap-y-2 mb-6">
                    {s.highlights.map((h, hi) => (
                      <li
                        key={h}
                        className="flex items-center gap-2 text-sm text-[#8D7B68]"
                      >
                        <Check
                          size={14}
                          className={hi % 2 === 0 ? "text-[#1A9BC1]" : "text-[#C99A4E]"}
                        />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <Cta item={s.primary} testId={`therapy-cta-primary-${i}`} />
                    {s.secondary && (
                      <Cta
                        item={s.secondary}
                        testId={`therapy-cta-secondary-${i}`}
                        variant={s.secondary.type === "paypal" ? "primary" : "secondary"}
                      />
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}

          {t.therapies.meditationAudio && (
            <Reveal delay={t.therapies.items.length * 120}>
              <a
                href={t.therapies.meditationAudio.url}
                target="_blank"
                rel="noreferrer"
                data-testid="meditation-audio-link"
                className="service-card !border-t-0 !border-l-0 h-full min-h-[420px] flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="section-number">04</span>
                    <span
                      className={`h-px w-16 ${t.therapies.items.length % 2 === 0 ? "bg-[#1A9BC1]" : "bg-[#C99A4E]"}`}
                    />
                  </div>
                  <div className="flex items-start gap-4 mb-3">
                    <div
                      aria-hidden
                      className="shrink-0 w-12 h-12 rounded-full bg-[#C99A4E] text-white flex items-center justify-center"
                    >
                      <Headphones size={20} />
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl text-[#3E362E] leading-tight">
                      {t.therapies.meditationAudio.title}
                    </h3>
                  </div>
                  <p className="overline !tracking-[0.18em] mb-5">
                    {t.therapies.meditationAudio.overline}
                  </p>
                  <p className="text-[#5E554C] text-base font-light leading-relaxed">
                    {t.therapies.meditationAudio.desc}
                  </p>
                </div>
                <div className="mt-8">
                  <span className="inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium text-[#C99A4E] border-b border-[#C99A4E]/40 group-hover:border-[#C99A4E] pb-1 transition-colors">
                    {t.therapies.meditationAudio.cta}
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </a>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
